import { oc } from "@orpc/contract";
import { z } from "zod";

// ─── Shared schemas ─────────────────────────────────────────────────────────

const adjustmentAuditFields = {
  id: z.string().uuid(),
  createdByUserId: z.string().uuid(),
  createdByUserName: z.string().nullable(),
  deactivatedAt: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.string(),
};

/** One demand-override row (history-preserving — includes revoked rows). */
export const DemandOverrideRowSchema = z.object({
  ...adjustmentAuditFields,
  /** `im_products_v3.key`: the SKU, and the only product identity procurement has. */
  productV3Key: z.string(),
  warehouseId: z.string().uuid(),
  periodMonth: z.string(), // YYYY-MM-01
  kind: z.enum(["history", "forecast"]),
  mode: z.enum(["zero", "average", "median", "value"]),
  value: z.number().nullable(),
  reason: z.string().nullable(),
});
export type DemandOverrideRow = z.infer<typeof DemandOverrideRowSchema>;

/** Forecast-derived policy levels — the before/after of a history-override preview. */
export const PolicyLevelsSchema = z.object({
  safetyStock: z.number(),
  reorderPoint: z.number(),
  orderUpToLevel: z.number(),
});
export type PolicyLevels = z.infer<typeof PolicyLevelsSchema>;

// A real calendar date (z.iso.date rejects e.g. 2026-99-01) constrained to the
// first of the month — the grain demand overrides are keyed on.
const periodMonth = z.iso.date().refine((v) => v.endsWith("-01"), {
  error: "periodMonth must be a first-of-month YYYY-MM-01.",
});

// ─── List (by product, optionally one warehouse) ─────────────────────────────

export const ListDemandOverridesInputSchema = z.object({
  productV3Key: z.string().min(1),
  warehouseId: z.string().uuid().optional(),
  /** Include revoked rows. Defaults to true server-side (the table shows history). */
  includeInactive: z.boolean().optional(),
});
export const ListDemandOverridesResponseSchema = z.object({
  overrides: z.array(DemandOverrideRowSchema),
});
export type ListDemandOverridesInput = z.infer<
  typeof ListDemandOverridesInputSchema
>;
export type ListDemandOverridesResponse = z.infer<
  typeof ListDemandOverridesResponseSchema
>;

// ─── Create ──────────────────────────────────────────────────────────────────

export const CreateDemandOverrideInputSchema = z
  .object({
    productV3Key: z.string().min(1),
    warehouseId: z.string().uuid(),
    periodMonth,
    kind: z.enum(["history", "forecast"]),
    mode: z.enum(["zero", "average", "median", "value"]),
    // Required when mode='value' (and always for kind='forecast'); ignored otherwise.
    value: z.number().nonnegative().nullable().optional(),
    reason: z.string().max(1000).nullable().optional(),
  })
  .refine((v) => v.kind !== "forecast" || v.mode === "value", {
    message: "A forecast override must use mode 'value'.",
    path: ["mode"],
  })
  .refine((v) => v.mode !== "value" || v.value != null, {
    message: "mode 'value' requires a value.",
    path: ["value"],
  });
export const CreateDemandOverrideResponseSchema = z.object({
  override: DemandOverrideRowSchema,
  /** Recomputed levels for a history override (null for forecast pins). */
  recomputed: PolicyLevelsSchema.nullable(),
});
export type CreateDemandOverrideInput = z.infer<
  typeof CreateDemandOverrideInputSchema
>;
export type CreateDemandOverrideResponse = z.infer<
  typeof CreateDemandOverrideResponseSchema
>;

// ─── Preview (history override before/after) ─────────────────────────────────

export const PreviewDemandOverrideInputSchema = z
  .object({
    productV3Key: z.string().min(1),
    warehouseId: z.string().uuid(),
    periodMonth,
    mode: z.enum(["zero", "average", "median", "value"]),
    value: z.number().nonnegative().nullable().optional(),
  })
  .refine((v) => v.mode !== "value" || v.value != null, {
    message: "mode 'value' requires a value.",
    path: ["value"],
  });
export const PreviewDemandOverrideResponseSchema = z.object({
  before: PolicyLevelsSchema,
  after: PolicyLevelsSchema,
  /** The concrete number the candidate month resolves to (mean/median/value/0). */
  appliedValue: z.number(),
});
export type PreviewDemandOverrideInput = z.infer<
  typeof PreviewDemandOverrideInputSchema
>;
export type PreviewDemandOverrideResponse = z.infer<
  typeof PreviewDemandOverrideResponseSchema
>;

// ─── Revoke ──────────────────────────────────────────────────────────────────

export const RevokeDemandOverrideInputSchema = z.object({
  overrideId: z.string().uuid(),
});
export const RevokeDemandOverrideResponseSchema = z.object({
  override: DemandOverrideRowSchema,
});
export type RevokeDemandOverrideInput = z.infer<
  typeof RevokeDemandOverrideInputSchema
>;
export type RevokeDemandOverrideResponse = z.infer<
  typeof RevokeDemandOverrideResponseSchema
>;

// ─── Individual historical sales adjustments ───────────────────────────────

export const ForecastSalesAdjustmentSchema = z.object({
  ...adjustmentAuditFields,
  mode: z.enum(["exclude", "quantity"]),
  adjustedQuantity: z.number().nullable(),
  comment: z.string().nullable(),
});
export type ForecastSalesAdjustment = z.infer<
  typeof ForecastSalesAdjustmentSchema
>;

export const ForecastSaleRowSchema = z.object({
  lineItemId: z.string().uuid(),
  salesOrderId: z.string().uuid(),
  orderReference: z.string(),
  lineNumber: z.number().int().nullable(),
  orderDate: z.iso.date(),
  customerName: z.string().nullable(),
  quantity: z.number(),
  sourceExcluded: z.boolean(),
  adjustments: z.array(ForecastSalesAdjustmentSchema),
});
export type ForecastSaleRow = z.infer<typeof ForecastSaleRowSchema>;

export const ListForecastSalesInputSchema = z.object({
  productV3Key: z.string().min(1),
  warehouseId: z.string().uuid(),
  periodMonth,
});
export const ListForecastSalesResponseSchema = z.object({
  sales: z.array(ForecastSaleRowSchema),
});
export type ListForecastSalesInput = z.infer<
  typeof ListForecastSalesInputSchema
>;
export type ListForecastSalesResponse = z.infer<
  typeof ListForecastSalesResponseSchema
>;

export const ForecastSalesAdjustmentSummarySchema = z.object({
  periodMonth,
  excludedCount: z.number().int().nonnegative(),
  quantityAdjustedCount: z.number().int().nonnegative(),
});
export type ForecastSalesAdjustmentSummary = z.infer<
  typeof ForecastSalesAdjustmentSummarySchema
>;

export const ListForecastSalesAdjustmentSummariesInputSchema = z.object({
  productV3Key: z.string().min(1),
  warehouseId: z.string().uuid(),
  periodFrom: periodMonth,
  periodTo: periodMonth,
});
export const ListForecastSalesAdjustmentSummariesResponseSchema = z.object({
  summaries: z.array(ForecastSalesAdjustmentSummarySchema),
});
export type ListForecastSalesAdjustmentSummariesInput = z.infer<
  typeof ListForecastSalesAdjustmentSummariesInputSchema
>;
export type ListForecastSalesAdjustmentSummariesResponse = z.infer<
  typeof ListForecastSalesAdjustmentSummariesResponseSchema
>;

export const AdjustForecastSaleInputSchema = z
  .object({
    salesOrderLineItemId: z.string().uuid(),
    mode: z.enum(["exclude", "quantity"]),
    adjustedQuantity: z.number().nonnegative().nullable().optional(),
    comment: z.string().max(1000).nullable().optional(),
  })
  .refine(
    (value) => value.mode !== "quantity" || value.adjustedQuantity != null,
    {
      message: "mode 'quantity' requires adjustedQuantity.",
      path: ["adjustedQuantity"],
    },
  );
export const AdjustForecastSaleResponseSchema = z.object({
  adjustment: ForecastSalesAdjustmentSchema,
});

export const RestoreForecastSaleInputSchema = z.object({
  adjustmentId: z.string().uuid(),
});
export const RestoreForecastSaleResponseSchema = z.object({
  adjustment: ForecastSalesAdjustmentSchema,
});

// ─── Contract ───────────────────────────────────────────────────────────────

export const demandOverridesContracts = {
  list: oc
    .input(ListDemandOverridesInputSchema)
    .output(ListDemandOverridesResponseSchema)
    .route({
      method: "GET",
      summary: "List demand overrides for a product",
      description:
        "Returns monthly history and forecast demand overrides for a SKU, active and revoked by default, optionally for one warehouse.",
    }),
  create: oc
    .input(CreateDemandOverrideInputSchema)
    .output(CreateDemandOverrideResponseSchema)
    .route({
      method: "POST",
      summary: "Create a monthly demand override",
      description:
        "Cleans a past month's demand (zero, average, median or value) or pins a future month's forecast for a SKU at a warehouse; history overrides recompute safety stock and reorder levels.",
    }),
  preview: oc
    .input(PreviewDemandOverrideInputSchema)
    .output(PreviewDemandOverrideResponseSchema)
    .route({
      method: "GET",
      summary: "Preview a demand history override",
      description:
        "Shows safety stock, reorder point and order-up-to level before and after a candidate monthly history override, plus the applied value, without saving.",
    }),
  revoke: oc
    .input(RevokeDemandOverrideInputSchema)
    .output(RevokeDemandOverrideResponseSchema)
    .route({
      method: "POST",
      summary: "Revoke a demand override",
      description:
        "Deactivates a monthly demand override immediately; revoking a history override recomputes that SKU-warehouse series' policy levels.",
    }),
  listSales: oc
    .input(ListForecastSalesInputSchema)
    .output(ListForecastSalesResponseSchema)
    .route({
      method: "GET",
      summary: "List sales behind a forecast month",
      description:
        "For a SKU, warehouse and month, lists the individual sales order lines feeding forecast history, with customer, quantity and any exclusion or quantity adjustments.",
    }),
  listSalesAdjustmentSummaries: oc
    .input(ListForecastSalesAdjustmentSummariesInputSchema)
    .output(ListForecastSalesAdjustmentSummariesResponseSchema)
    .route({
      method: "GET",
      summary: "Count forecast sales adjustments by month",
      description:
        "For a SKU at a warehouse over a month range, counts active sales-line exclusions and quantity adjustments per month.",
    }),
  adjustSale: oc
    .input(AdjustForecastSaleInputSchema)
    .output(AdjustForecastSaleResponseSchema)
    .route({
      method: "POST",
      summary: "Exclude or adjust a sale in forecast history",
      description:
        "Excludes one sales order line from forecast demand history or replaces its quantity, with an optional comment. The source sale is not changed.",
    }),
  restoreSale: oc
    .input(RestoreForecastSaleInputSchema)
    .output(RestoreForecastSaleResponseSchema)
    .route({
      method: "POST",
      summary: "Restore a sale in forecast history",
      description:
        "Removes an exclusion or quantity adjustment so the sales line counts at its original quantity in forecast demand history again.",
    }),
} as const;
